import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { apiGet, type ApiError } from '../lib/api';

export type TimelineEventType =
  | 'check_in'
  | 'reflection'
  | 'chat_message'
  | 'expansion_completed'
  | 'practice_completed'
  | 'synthesis_run'
  | 'subscription_event'
  | 'milestone';

export const TIMELINE_EVENT_TYPES: TimelineEventType[] = [
  'check_in',
  'reflection',
  'chat_message',
  'expansion_completed',
  'practice_completed',
  'synthesis_run',
  'subscription_event',
  'milestone',
];

export interface TimelineEvent {
  type: TimelineEventType;
  occurred_at: string;
  /** On a chat_message row this is the MESSAGE id, not the conversation. */
  resource_id: string;
  /** Set on chat_message rows only: the id the transcript read needs. Null otherwise. */
  conversation_id: string | null;
  summary: string;
}

export interface UserOverview {
  profile: {
    user_id: string;
    email: string;
    display_name: string | null;
    created_at: string;
    deleted_at: string | null;
    subscription_tier: string;
    subscription_status: string;
    has_completed_onboarding: boolean;
    is_sim: boolean;
    is_admin: boolean;
    user_timezone: string;
    compass_words: string[];
    onboarding_q1: string | null;
    onboarding_q2: string | null;
    mentor_id: string | null;
  };
  streak: { current_streak: number; longest_streak: number } | null;
  counts: Record<string, number>;
  latest_synthesis: { status: string; completed_at: string | null } | null;
  /** SPRINT-0029 [8A-3] — this UTC month's model spend against the member monthly cap. */
  usage: MemberUsage;
}

/** `GET /admin/users/:id` → `usage`. Integer micro-USD; `cap_micro_usd` null means no cap is set. */
export interface MemberUsage {
  month: string;
  micro_usd: number;
  calls: number;
  cap_micro_usd: number | null;
  by_surface: { surface: string; micro_usd: number; calls: number }[];
}

// The `as '/admin/users/{id}'` casts exist because apiGet's key is a literal path
// template while the runtime value is interpolated. A path-builder helper would be
// cleaner; it is not worth the indirection for four call sites.
export function useUserOverview(id: string) {
  return useQuery<UserOverview, ApiError>({
    queryKey: ['admin', 'user', id],
    queryFn: () => apiGet(`/admin/users/${id}` as '/admin/users/{id}') as Promise<UserOverview>,
  });
}

export interface TimelineFilters {
  types: TimelineEventType[];
  from: string | null;
  to: string | null;
}

export function useTimeline(id: string, filters: TimelineFilters) {
  return useInfiniteQuery<{ events: TimelineEvent[]; next_cursor: string | null }, ApiError>({
    queryKey: ['admin', 'timeline', id, filters],
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    queryFn: ({ pageParam }) =>
      apiGet(`/admin/users/${id}/timeline` as '/admin/users/{id}/timeline', {
        types: filters.types.length > 0 ? filters.types : undefined,
        from: filters.from ?? undefined,
        to: filters.to ?? undefined,
        cursor: (pageParam as string | null) ?? undefined,
        limit: 100,
      }) as Promise<{ events: TimelineEvent[]; next_cursor: string | null }>,
  });
}
